import { useCallback, useEffect, useState } from "react";
import type {
  WebAnalyticsReport,
  WebFunnelStepKey,
} from "@care-by-ella/contracts";
import { fetchWebAnalytics } from "@/lib/web-analytics-api";

// TES-928 — visits to the public web (ella.se), recorded server-side without
// cookies. Every number is a count; a "visitor" is one browser on one day,
// because the hash that groups a visitor's views changes every day by design.

const fmt = new Intl.NumberFormat("sv-SE");
const DAY_MS = 24 * 60 * 60 * 1000;

const PERIODS = [
  { days: 7, label: "7 dagar" },
  { days: 30, label: "30 dagar" },
  { days: 90, label: "90 dagar" },
  { days: 365, label: "12 månader" },
] as const;

const FUNNEL_LABEL: Record<WebFunnelStepKey, string> = {
  home: "Startsidan",
  bmi: "BMI-testet",
  subscribe: "Abonnemangssidan",
  checkout: "Kassan",
  checkout_success: "Köp genomfört",
};

const DEVICE_LABEL: Record<string, string> = {
  mobile: "Mobil",
  tablet: "Surfplatta",
  desktop: "Dator",
};

// A UTC day on purpose (TES-1609): the api counts page views per UTC day
// (`utcDay` in web-analytics.service.ts), so a Stockholm "today" would ask
// for a day that has no rows yet in the first hour after Swedish midnight.
function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeFor(days: number): { from: string; to: string } {
  const to = new Date();
  return { from: isoDay(new Date(to.getTime() - (days - 1) * DAY_MS)), to: isoDay(to) };
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="border border-surfaceMuted rounded-md p-md bg-surface flex flex-col gap-xs min-w-0">
      <p className="text-xs uppercase tracking-wide text-textSecondary">{label}</p>
      <p className="font-display text-3xl tabular-nums">{fmt.format(value)}</p>
      {hint ? <p className="text-xs text-textSecondary">{hint}</p> : null}
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-sm">
      <div className="flex flex-col gap-xs">
        <h2 className="text-sm font-medium">{title}</h2>
        {note ? <p className="text-xs text-textSecondary max-w-xl">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

// A two- or three-column count table with a proportional bar behind the
// first number, so the ranking reads at a glance without a chart library.
function CountTable({
  head,
  rows,
  empty,
}: {
  head: [string, string, string?];
  rows: { key: string; label: string; value: number; extra?: number }[];
  empty: string;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-textSecondary border border-surfaceMuted rounded-md p-md bg-surface">
        {empty}
      </p>
    );
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">{head[0]}</th>
            <th className="px-md py-sm font-medium text-right">{head[1]}</th>
            {head[2] ? (
              <th className="px-md py-sm font-medium text-right">{head[2]}</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-surfaceMuted">
              <td className="px-md py-sm break-words">{r.label}</td>
              <td className="px-md py-sm text-right tabular-nums w-40">
                <div className="flex items-center justify-end gap-sm">
                  <div
                    className="h-2 rounded-full bg-brand/40"
                    style={{ width: `${Math.max(4, (r.value / max) * 80)}px` }}
                    aria-hidden="true"
                  />
                  {fmt.format(r.value)}
                </div>
              </td>
              {head[2] ? (
                <td className="px-md py-sm text-right tabular-nums text-textSecondary">
                  {fmt.format(r.extra ?? 0)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Funnel({ funnel }: { funnel: WebAnalyticsReport["funnel"] }) {
  const top = funnel[0]?.visitorDays ?? 0;
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Steg</th>
            <th className="px-md py-sm font-medium text-right">Besökare</th>
            <th className="px-md py-sm font-medium text-right">Andel av startsidan</th>
          </tr>
        </thead>
        <tbody>
          {funnel.map(({ step, visitorDays }) => (
            <tr key={step} className="border-t border-surfaceMuted">
              <td className="px-md py-sm">{FUNNEL_LABEL[step]}</td>
              <td className="px-md py-sm text-right tabular-nums">{fmt.format(visitorDays)}</td>
              <td className="px-md py-sm text-right tabular-nums text-textSecondary">
                {top > 0 ? `${Math.round((visitorDays / top) * 100)} %` : "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function WebAnalyticsRoute() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<WebAnalyticsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setData(null);
    try {
      const { from, to } = rangeFor(days);
      setData(await fetchWebAnalytics(from, to));
    } catch (err) {
      setError(err instanceof Error ? err.message : "okänt fel");
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <div className="flex items-start justify-between gap-md flex-wrap">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">Webbstatistik</h1>
        <p className="text-textSecondary text-sm max-w-xl">
          Besök på ella.se, mätta på vår egen server utan cookies. En besökare
          räknas per dygn: samma person två dagar i rad räknas två gånger.
          Sparas i högst 13 månader.
        </p>
      </div>
      <div className="flex gap-xs shrink-0" role="group" aria-label="Period">
        {PERIODS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => setDays(p.days)}
            aria-pressed={days === p.days}
            className={`px-md py-sm rounded-md border text-sm ${
              days === p.days
                ? "border-brand bg-brand/10"
                : "border-surfaceMuted"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div className="border border-danger rounded-md p-md text-sm bg-surface flex flex-col gap-sm items-start">
          <p className="text-danger">Statistiken kunde inte hämtas ({error}).</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div className="grid gap-sm grid-cols-2 md:grid-cols-4" aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className="h-24 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  const busiest = data.daily.reduce(
    (best, d) => (d.visitors > (best?.visitors ?? -1) ? d : best),
    null as WebAnalyticsReport["daily"][number] | null,
  );

  return (
    <div className="flex flex-col gap-lg">
      {header}

      {data.totals.views === 0 ? (
        <div className="border border-surfaceMuted rounded-md p-lg bg-surface flex flex-col gap-sm items-start">
          <p className="text-sm font-medium">Inga besök under perioden</p>
          <p className="text-sm text-textSecondary max-w-lg">
            Siffrorna fylls på när någon besöker ella.se. Botar och sökmotorer
            räknas inte.
          </p>
        </div>
      ) : null}

      <div className="grid gap-sm grid-cols-2 md:grid-cols-4">
        <Stat label="Sidvisningar" value={data.totals.views} />
        <Stat label="Besökare" value={data.totals.visitorDays} hint="Summa per dygn" />
        <Stat
          label="Snitt per dag"
          value={Math.round(data.totals.visitorDays / Math.max(1, days))}
          hint="Besökare"
        />
        <Stat
          label="Bästa dagen"
          value={busiest?.visitors ?? 0}
          hint={busiest ? busiest.day : "–"}
        />
      </div>

      <Section
        title="Vägen till ett köp"
        note="Samma dygn, inte strikt i ordning: den som går direkt till kassan räknas där utan att ha räknats på startsidan. Svenska och engelska sidor räknas ihop."
      >
        <Funnel funnel={data.funnel} />
      </Section>

      <Section title="Mest besökta sidor">
        <CountTable
          head={["Sida", "Visningar", "Besökare"]}
          rows={data.topPaths.map((p) => ({
            key: p.path,
            label: p.path,
            value: p.views,
            extra: p.visitors,
          }))}
          empty="Inga sidvisningar ännu."
        />
      </Section>

      <div className="grid gap-lg md:grid-cols-2">
        <Section title="Varifrån besökarna kommer">
          <CountTable
            head={["Webbplats", "Visningar"]}
            rows={data.referrers.map((r) => ({
              key: r.label,
              label: r.label,
              value: r.views,
            }))}
            empty="Inga hänvisningar från andra webbplatser."
          />
        </Section>
        <Section title="Enheter">
          <CountTable
            head={["Enhet", "Visningar"]}
            rows={data.devices.map((d) => ({
              key: d.label,
              label: DEVICE_LABEL[d.label] ?? d.label,
              value: d.views,
            }))}
            empty="Inga sidvisningar ännu."
          />
        </Section>
      </div>

      <Section
        title="Kampanjer"
        note="Länkar med utm_source, utm_medium eller utm_campaign."
      >
        <CountTable
          head={["Källa / medium / kampanj", "Visningar"]}
          rows={data.campaigns.map((c) => {
            const label = [c.source, c.medium, c.campaign]
              .map((v) => v ?? "–")
              .join(" / ");
            return { key: label, label, value: c.views };
          })}
          empty="Inga kampanjlänkar under perioden."
        />
      </Section>

      <Section title="Per dag">
        <CountTable
          head={["Dag", "Besökare", "Visningar"]}
          rows={[...data.daily].reverse().map((d) => ({
            key: d.day,
            label: d.day,
            value: d.visitors,
            extra: d.views,
          }))}
          empty="Inga besök under perioden."
        />
      </Section>
    </div>
  );
}
